import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ApizStepperData, ItemStateType, RunWorkflowResponse } from '@compiere-ws/models/apiz-domains';
import { DataStore, DataStoreName, DataStoreStatus } from '@compiere-ws/models/compiere-data-json';
import { CompiereWorkflowService } from '@compiere-ws/services/compiere-workflow/compiere-workflow.service';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { MessageManagerService } from '@iupics-manager/managers/message/message-manager.service';
import { AbstractDataContainer, AbstractDataContainerType } from '@iupics-manager/models/abstract-datacontainer';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsData } from '@iupics-manager/models/iupics-data';
import { IupicsEvent } from '@iupics-manager/models/iupics-event';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { LogicEvaluator } from '@iupics-util/tools/logic-evaluator';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import PrimeStepsComponent from '../../../overrided/prime-steps/prime-steps.component';

@Component({
  selector: 'iu-stepper-ui',
  templateUrl: './stepper-ui.component.html',
  styleUrls: ['./stepper-ui.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [PrimeStepsComponent],
})
export default class StepperUiComponent extends AbstractDataContainer implements OnInit, AfterViewInit {
  #translateService = inject(TranslateService);
  #messageManager = inject(MessageManagerService);
  #workflowService = inject(CompiereWorkflowService);

  @Output()
  refreshEdit: EventEmitter<any> = new EventEmitter();
  @Input()
  items: MenuItem[] = [];
  @Input()
  activeIndex = 0;

  @Input() data: ApizStepperData & IupicsData;

  @Input()
  isIndexNumber = true;

  @Input()
  preventPrevious = false;
  @Input()
  parent: EditViewUiComponent;

  @Input()
  editTab: EditTabUiComponent;
  @Input() changingStepper: Subject<any>;

  oldIndex: number;
  forbiddenStatus: string[] = ['CL', 'VO', 'RE'];
  cancelNext = false;
  isLoaded: boolean;
  isDisplay = true;

  /** Stored separately from subscriptions[] because it needs manual unsubscription on each status change. */
  private nextStatusSubscription: Subscription | undefined;

  /** Preserved across the reject-reason modal flow. */
  private itemId: string;
  private windowCtx: Record<string, any>;

  readonly R_REQUEST_TABLE_ID = 417;
  /** Table ID for the invoice (C_Invoice). Kept as a named constant to avoid magic numbers. */
  private readonly INVOICE_TABLE_ID = 318;

  containerType: AbstractDataContainerType = AbstractDataContainerType.STEPPER;

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit() {
    Global.startPerf(this);
    if (this.changingStepper) {
      this.items = [
        {
          label: this.#translateService.instant('generic.loading') + '...',
          id: this.#translateService.instant('generic.loading'),
        },
      ];
      this.subscriptions.push(
        this.changingStepper.subscribe((currentStore) => {
          if (currentStore && currentStore.status !== DataStoreStatus.NEWRECORD) {
            this.dataStored = currentStore;
            this.getNextStatus(currentStore);
          } else {
            this.oldIndex = 0;
            if (currentStore?.status === DataStoreStatus.NEWRECORD && currentStore?.data?.DocStatus) {
              this.items = [this.getCurrentStatusItem(currentStore)];
            } else {
              this.items = [];
            }
          }
        })
      );
    }
    if (this.parent && this.parent.currentDataStoreKey) {
      const currentStore = this.store.getStore(this.parent.currentDataStoreKey, DataStoreName.CURRENT);
      if (currentStore) {
        this.dataStored = <DataStore>currentStore;
        this.getNextStatus(currentStore);
        this.oldIndex = this.activeIndex;
      }
    }
    if (this.editTab) this.editTab.currentStepper = this;
  }


  /** Single-item MenuItem representing the current document status. */
  private getCurrentStatusItem(store: any): MenuItem {
    return { label: this.getDisplayStatus(store), id: this.getIDStatus(store) };
  }

  /** Shared params for getNextStatus service calls to avoid duplication. */
  private buildNextStatusParams(store: DataStore) {
    return {
      docAction: store.data.DocAction.id,
      docStatus: this.getIDStatus(store),
      isSOTrx: store.data.IsSOTrx,
      orderType: '',
      processing: store.data.Processing,
      record_ID: this.currentRecordId,
      table_ID: String(this.parent.editTabs[0].data.AD_Table_ID),
      table_Name: String(this.parent.editTabs[0].data.tableName),
      user_ID: String(this.connectorService.getIupicsUserContext()['##AD_User_ID']),
    };
  }

  /** Resolves a human-readable subject line from the record data. */
  private resolveSubject(data: Record<string, any>): string {
    return (
      data['Name'] ??
      data['Value'] ??
      data['DocumentNo'] ??
      `ID : ${this.currentRecordId}`
    );
  }

  /** Current record ID extracted from the composite store key. */
  private get currentRecordId(): string {
    return this.parent.currentDataStoreKey.recordId.split(',').pop();
  }

  /**
   * Fetches available next statuses relative to the current document status,
   * unless the record is new or transitions are cancelled.
   */
  getNextStatus(currentStore?: any) {
    this.checkReadAndDisplay(currentStore);

    if (!this.isLoaded) {
      if (currentStore.data?.DocStatus) {
        this.items = [this.getCurrentStatusItem(currentStore)];
      }
      return;
    }

    if (this.cancelNext) {
      this.cancelNext = false;
      return;
    }

    this.nextStatusSubscription?.unsubscribe();

    if (this.isReadOnly) {
      if (currentStore.data?.DocStatus instanceof Object) {
        this.items = [this.getCurrentStatusItem(currentStore)];
      }
      return;
    }

    const { data } = currentStore;
    if (data?.DocAction && data?.DocStatus && !this.forbiddenStatus.includes(data.DocStatus.id)) {
      this.nextStatusSubscription = this.#workflowService
        .getNextStatus(this.buildNextStatusParams(currentStore))
        .subscribe((nextStatus) => {
          this.items = [
            this.getCurrentStatusItem(currentStore),
            ...nextStatus.map((s) => ({ label: s.Name, id: s.Value })),
          ];
          this.activeIndex = this.items.findIndex((s) => s.id === this.getIDStatus(currentStore));
          currentStore.dataChange.emit({ dataModified: 'WV' });
        });
    } else if (data?.DocStatus) {
      this.items = [this.getCurrentStatusItem(currentStore)];
    }
  }

  /**
   * Triggered when the user selects a stepper item. Saves if needed, validates
   * mandatory fields, then runs the workflow.
   */
  changeItem(index: number): void {
    if (!this.isReadOnly && index > this.activeIndex) {
      this.isReadOnly = true;
      const itemId = this.items[index].id as string;

      if (this.parent && !this.store.checkDataBeforeNewLine(this.parent.currentDataStoreKey)) {
        this.cancelNext = true;
        this.parent.beforeSave(null).then(() => this.runWF(itemId, this.parent.getCurrentContext()));
      } else if (this.parent && !this.parent.checkFieldMandatoryAndFormat()) {
        this.activeIndex = this.oldIndex;
        this.isReadOnly = false;
      } else {
        this.runWF(itemId, this.parent.getCurrentContext());
      }
    }
  }

  /**
   * Routes to reject-reason modal for e-invoice refusals (table 318, non-SOTrx),
   * otherwise executes the workflow directly.
   */
  runWF(itemId: string, windowCtx: any): void {
    if (
      itemId === 'IV' &&
      this.parent.table_id === this.INVOICE_TABLE_ID &&
      this.editTab.dataStored.data['IsSOTrx'] === 'N' &&
      this.editTab.dataStored.data['EInvoiceID'] !== null &&
      this.editTab.dataStored.data['Status'] !== null
    ) {
      this.activeIndex = this.oldIndex;
      this.isReadOnly = false;
      this.itemId = itemId;
      this.windowCtx = windowCtx;
      this.parent.updateModalDisplayByValue(
        { key: 'displayFormUI', value: true, sourceComponent: this },
        { key: 'formId', value: 'RejectReasonEinvoince' }
      );
    } else {
      this.runWFExecute(itemId, windowCtx);
    }
  }

  /** Called by the reject-reason form to inject extra context then run the workflow. */
  runWFRejectReason(Status: string, E_RejectReason_ID: any, Message: string): void {
    this.windowCtx['E_RejectReason_ID'] = E_RejectReason_ID;
    this.windowCtx['Status'] = Status;
    this.windowCtx['Message'] = Message;
    this.runWFExecute(this.itemId, this.windowCtx);
  }

  runWFExecute(itemId: string, windowCtx: any): void {
    const currentEditTab = this.parent.editTabs.find((t) => t.tabId === this.parent.tabId);
    const ad_process_id = currentEditTab.data.workflow.AD_Process_ID;
    const dataStored = currentEditTab.dataStored;
    const windowId = this.parent.data.AD_Window_ID;

    const subject = this.parent.data.label + ' — ' + this.resolveSubject(dataStored?.data ?? {});
    windowCtx['#WindowId'] = windowId;
    windowCtx['#Subject'] = subject;

    this.#workflowService
      .runWF({
        record_id: this.currentRecordId,
        windowCtx,
        action: String(itemId),
        table_Name: String(this.parent.editTabs[0].data.tableName),
        table_id: String(this.parent.editTabs[0].data.AD_Table_ID),
        ad_process_id: String(ad_process_id),
      })
      .pipe(
        switchMap((response: RunWorkflowResponse) => {
          if (!response) return of(response);

          if (response.success === true) {
            this.handleSuccessResponse(response, dataStored);

            if (
              dataStored.data?.DocAction &&
              dataStored.data?.DocStatus &&
              !this.forbiddenStatus.includes(dataStored.data.DocStatus.id)
            ) {
              return this.#workflowService.getNextStatus(this.buildNextStatusParams(dataStored)).pipe(
                tap((nextStatus) => {
                  this.items = [
                    this.getCurrentStatusItem(dataStored),
                    ...nextStatus.map((s) => ({ label: s.Name, id: s.Value })),
                  ];
                  this.activeIndex = this.items.findIndex((s) => s.id === this.getIDStatus(dataStored));
                  dataStored.dataChange.emit({ dataModified: 'WV' });
                  this.cancelNext = true;
                }),
                map(() => response)
              );
            } else {
              this.items = [this.getCurrentStatusItem(dataStored)];
              this.activeIndex = this.items.findIndex((s) => s.id === this.getIDStatus(dataStored));
              dataStored.dataChange.emit({ dataModified: 'WV' });
              this.cancelNext = true;
              return of(response);
            }
          } else {
            if (response.processed === 'Y' && response.message) {
              this.#messageManager.newMessage(
                new IupicsMessage(this.#translateService.instant('generic.success'), response.message, 'success')
              );
            } else {
              this.activeIndex = this.oldIndex;
              this.#messageManager.newMessage(
                new IupicsMessage(this.#translateService.instant('generic.warning'), response.message || ' no response')
              );
            }
          }

          return of(response);
        }),
        tap((response) => {
          if (response) this.checkReadAndDisplay(dataStored);
        }),
        finalize(() => this.refreshEdit.emit())
      )
      .subscribe();
  }

  /** Emits a success or warning message and updates DocStatus/DocAction on the store. */
  private handleSuccessResponse(response: RunWorkflowResponse, dataStored: DataStore): void {
    const initialStatus = this.items[0];
    const sameStatus = initialStatus?.id === response.result?.['DocStatus']?.['id'];
    const hasMessage = response.message != null;

    if ((response.processed === 'N' && hasMessage) || (sameStatus && hasMessage)) {
      this.#messageManager.newMessage(
        new IupicsMessage(this.#translateService.instant('generic.warning'), response.message, 'warning')
      );
    } else {
      this.#messageManager.newMessage(
        new IupicsMessage(this.#translateService.instant('generic.success'), response.message ?? '', 'success')
      );
    }

    dataStored.data.DocStatus = response.result?.['DocStatus'];
    dataStored.data.DocAction = response.result?.['DocAction'];
  }

  // ─── Field state ──────────────────────────────────────────────────────────

  /** Updates isDisplay and isLoaded (both driven by the display logic observable) and isReadOnly. */
  checkReadAndDisplay(dataStored: DataStore) {
    this.isFieldDisplay(dataStored).subscribe((displayed) => {
      this.isDisplay = displayed;
      this.isLoaded = displayed;
    });
    this.isReadOnly = this.isFieldReadOnly(dataStored);
  }

  isFieldDisplay(dataStored: DataStore, changedColumns?: any): Observable<boolean> {
    const docActionData = this.data?.['docActionField']?.['data'];
    if (docActionData) {
      if (docActionData.displayLogic) {
        const windowCtx = this.getCurrentContext(dataStored, false);
        if (docActionData.displayLogic.trim().toLowerCase().startsWith('@sql=')) {
          return this.fieldStateService
            .getEntityState({
              fieldType: this.fieldType,
              id: this.data.fieldId ?? this.data.columnId,
              states: [ItemStateType.DISPLAYED],
              windowCtx,
            })
            .pipe(map((result) => result?.displayed));
        } else {
          return of(LogicEvaluator.evaluateLogic(windowCtx, docActionData.displayLogic));
        }
      } else {
        return of(true);
      }
    } else if (this.data?.['docStatusField']?.['data']) {
      return of(true);
    } else {
      return of(false);
    }
  }

  /**
   * Returns whether the stepper should be read-only.
   *
   * Note: when a SQL-based readOnlyLogic is present, this method returns `false`
   * synchronously and updates `this.isReadOnly` asynchronously via a subscription.
   */
  isFieldReadOnly(dataStored: DataStore): boolean {
    const docStatus = dataStored?.data?.DocStatus;
    const isNewRecord = dataStored.status === DataStoreStatus.NEWRECORD;

    // Standard forbidden statuses for all tables
    if ((docStatus && this.parent.table_id !== this.INVOICE_TABLE_ID && this.forbiddenStatus.includes(docStatus.id)) || isNewRecord) {
      return true;
    }

    // Invoice-specific forbidden statuses
    if (docStatus && this.parent.table_id === this.INVOICE_TABLE_ID) {
      if (['CL', 'VO'].includes(docStatus.id)) return true;
      if (docStatus.id === 'RE' && dataStored.data['IsSOTrx'] === 'N') return true;
    }

    const docActionData = this.data?.['docActionField']?.['data'];
    if (docActionData) {
      if (docActionData.isAlwaysUpdatable === true) return false;
      if (this.editTab?.isReadOnly) return true;
      if (docActionData.isButtonReadOnly === true) return true;
      if (docActionData.isUpdateable === false && dataStored) return true;
      if (!this.canUpdate(dataStored)) return true;

      const readOnlyLogic: string | undefined = docActionData.readOnlyLogic;
      if (readOnlyLogic) {
        const windowCtx = this.getCurrentContext(dataStored, false);
        if (readOnlyLogic.trim().toLowerCase().startsWith('@sql=')) {
          // Async path: returns false now, sets isReadOnly once the result arrives.
          this.fieldStateService
            .getEntityState({
              fieldType: this.fieldType,
              id: this.data.fieldId ?? this.data.columnId,
              states: [ItemStateType.READONLY],
              windowCtx,
            })
            .subscribe((result) => (this.isReadOnly = result?.readonly ?? false));
        } else if (LogicEvaluator.evaluateLogic(windowCtx, readOnlyLogic)) {
          return true;
        }
      }

      if (dataStored.data.IsActive === 'N' && !this.isAccordion) return true;
      return false;
    } else if (this.data?.['docStatusField']?.['data']) {
      return true;
    } else {
      return false;
    }
  }

  getIDStatus(currentStore?: any): string {
    return this.editTab.data.AD_Table_ID === this.R_REQUEST_TABLE_ID
      ? currentStore.data.R_Status_ID.id
      : currentStore.data.DocStatus.id;
  }

  getDisplayStatus(currentStore?: any): string {
    return this.editTab.data.AD_Table_ID === this.R_REQUEST_TABLE_ID
      ? currentStore.data.R_Status_ID.displayValue
      : currentStore.data.DocStatus.displayValue;
  }
  onChildUpdate(event): void {}
  onSiblingUpdate(event: IupicsEvent): void {}
  onRemoveComponent(event: IupicsEvent): void {}
}
